import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { HeartHandshake, Building2, User, Users, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const joinCode = searchParams.get("join") || "";
  const refCode = searchParams.get("ref") || "";
  useEffect(() => {
    if (refCode) localStorage.setItem("rb_ref", refCode);
  }, [refCode]);
  const [mode, setMode] = useState(joinCode ? "join" : "login");
  const [accountType, setAccountType] = useState("individual");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [code, setCode] = useState(joinCode);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register, joinOrg } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode !== "login" && password !== confirmPassword) {
      setError("Passwords don't match — please retype them.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else if (mode === "join") await joinOrg(code, name, email, password);
      else await register(name, email, password, accountType, orgName);
      const circleCode = searchParams.get("circle");
      if (circleCode) {
        try {
          const { data: joined } = await api.post("/care-circle/join", { code: circleCode });
          toast.success(`You're now in ${joined.name}'s care circle`);
        } catch (ce) {
          toast.error(apiError(ce));
        }
      }
      navigate("/app");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full min-h-[56px] rounded-2xl border-2 border-foreground/25 bg-white px-4 text-lg focus:outline-none focus:border-primary";

  const titles = {
    login: ["Welcome back", "Log in to your account."],
    signup: ["Create your account", "Individuals start free. No card needed."],
    join: ["Join your team", "Enter the invite code from your organisation."],
  };

  const typeCard = (active) =>
    `flex-1 min-h-[88px] rounded-2xl border-2 p-3 flex flex-col items-center justify-center gap-1 font-bold text-base transition-colors duration-200 ${
      active ? "border-primary bg-primary/10" : "border-foreground/20 bg-white hover:bg-secondary"
    }`;

  return (
    <div data-testid="auth-page" className="min-h-screen bg-background flex flex-col">
      <header className="max-w-5xl w-full mx-auto px-4 py-4">
        <Link to="/" data-testid="auth-home-link" className="flex items-center gap-2 w-fit">
          <span className="w-10 h-10 rounded-2xl bg-primary grid place-items-center">
            <HeartHandshake size={24} strokeWidth={2.5} className="text-white" />
          </span>
          <span className="text-xl font-extrabold tracking-tight">RoutineBuddy</span>
        </Link>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 pt-8 pb-12">
        <div className="w-full max-w-md bg-white rounded-3xl border-2 border-foreground/10 p-8">
          <h1 className="text-2xl sm:text-3xl font-extrabold">{titles[mode][0]}</h1>
          <p className="mt-2 text-lg text-muted-foreground">{titles[mode][1]}</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            {mode === "signup" && (
              <div className="flex gap-3">
                <button type="button" data-testid="account-type-individual" onClick={() => setAccountType("individual")} className={typeCard(accountType === "individual")}>
                  <User size={28} strokeWidth={2.5} /> Individual carer
                </button>
                <button type="button" data-testid="account-type-business" onClick={() => setAccountType("business")} className={typeCard(accountType === "business")}>
                  <Building2 size={28} strokeWidth={2.5} /> Care organisation
                </button>
              </div>
            )}
            {mode === "join" && (
              <div>
                <label htmlFor="invite-code" className="block text-base font-bold mb-1">Invite code</label>
                <input id="invite-code" data-testid="invite-code-input" value={code} onChange={(e) => setCode(e.target.value)} required className={inputCls} placeholder="e.g. a1b2c3d4e5" />
              </div>
            )}
            {mode !== "login" && (
              <div>
                <label htmlFor="name" className="block text-base font-bold mb-1">Your name</label>
                <input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} placeholder="e.g. Emmanuel" />
              </div>
            )}
            {mode === "signup" && accountType === "business" && (
              <div>
                <label htmlFor="org-name" className="block text-base font-bold mb-1">Organisation name</label>
                <input id="org-name" data-testid="org-name-input" value={orgName} onChange={(e) => setOrgName(e.target.value)} required className={inputCls} placeholder="e.g. Sunrise Care Home" />
              </div>
            )}
            <div>
              <label htmlFor="email" className="block text-base font-bold mb-1">Email</label>
              <input id="email" type="email" data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} placeholder="you@example.com" />
            </div>
            <div>
              <label htmlFor="password" className="block text-base font-bold mb-1">Password</label>
              <div className="relative">
                <input id="password" type={showPw ? "text" : "password"} data-testid="password-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className={`${inputCls} pr-14`} placeholder="At least 8 characters" />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] grid place-items-center rounded-full hover:bg-secondary transition-colors duration-200">
                  {showPw ? <EyeOff size={22} strokeWidth={2.5} /> : <Eye size={22} strokeWidth={2.5} />}
                </button>
              </div>
            </div>
            {mode !== "login" && (
              <div>
                <label htmlFor="confirm-password" className="block text-base font-bold mb-1">Confirm password</label>
                <div className="relative">
                  <input id="confirm-password" type={showConfirmPw ? "text" : "password"} data-testid="confirm-password-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} className={`${inputCls} pr-14`} placeholder="Type it again" />
                  <button type="button" data-testid="toggle-confirm-visibility" onClick={() => setShowConfirmPw((v) => !v)} aria-label={showConfirmPw ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] grid place-items-center rounded-full hover:bg-secondary transition-colors duration-200">
                    {showConfirmPw ? <EyeOff size={22} strokeWidth={2.5} /> : <Eye size={22} strokeWidth={2.5} />}
                  </button>
                </div>
              </div>
            )}
            {mode === "login" && (
              <div className="text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-primary font-bold text-base">Forgot password?</Link>
              </div>
            )}
            {error && <p data-testid="auth-error" className="text-[#C53030] font-semibold bg-[#F56565]/10 border-2 border-[#F56565]/40 rounded-2xl px-4 py-3">{error}</p>}
            <button
              type="submit"
              data-testid="auth-submit-button"
              disabled={busy}
              className="w-full min-h-[56px] rounded-full bg-primary text-white text-lg font-bold transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-60"
            >
              {busy ? "Please wait…" : mode === "login" ? "Log in" : mode === "join" ? "Join team" : accountType === "business" ? "Create organisation account" : "Create free account"}
            </button>
          </form>

          <button
            data-testid="auth-toggle-mode-button"
            onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }}
            className="mt-5 w-full min-h-[48px] text-primary font-bold text-base"
          >
            {mode === "login" ? "New here? Create an account" : "Already have an account? Log in"}
          </button>
          {mode !== "join" && (
            <button
              data-testid="join-team-link"
              onClick={() => { setMode("join"); setError(""); }}
              className="w-full min-h-[48px] text-muted-foreground font-bold text-base flex items-center justify-center gap-2"
            >
              <Users size={18} strokeWidth={2.5} /> Staff member? Join with an invite code
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
